import type { FinalizeData } from '@cloudflare/voprf-ts';
import { Evaluation, EvaluationRequest, VOPRFClient } from '@cloudflare/voprf-ts';

import { fromBase64Url, toBase64Url } from '../../bytes';

import { VOPRF_SUITE } from './types';

/**
 * VOPRF client session
 * Manages blinding, proof verification, and finalization
 */
export class VoprfSession {
  private readonly client: VOPRFClient;
  private readonly finalizeData: FinalizeData;
  private readonly evaluationRequest: EvaluationRequest;

  private constructor(
    client: VOPRFClient,
    finalizeData: FinalizeData,
    request: EvaluationRequest,
  ) {
    this.client = client;
    this.finalizeData = finalizeData;
    this.evaluationRequest = request;
  }

  /**
   * Create a new VOPRF session
   * Blinds the input for server evaluation
   *
   * @param serverPublicKey - Server's VOPRF public key
   * @param input - Input bytes to blind
   * @returns New session
   */
  static async create(
    serverPublicKey: Uint8Array,
    input: Uint8Array,
  ): Promise<VoprfSession> {
    const client = new VOPRFClient(VOPRF_SUITE, serverPublicKey);
    const [finalizeData, request] = await client.blind([input]);
    return new VoprfSession(client, finalizeData, request);
  }

  /**
   * Get blinded request as base64url string
   */
  getRequestBase64(): string {
    return toBase64Url(this.evaluationRequest.serialize());
  }

  /**
   * Verify server's proof (constant-time comparison)
   *
   * @param evaluatedBase64 - Server's evaluation (base64url)
   * @param proofBase64 - Server's proof (base64url)
   * @throws {Error} if proof is invalid
   */
  verifyProof(evaluatedBase64: string, proofBase64: string): void {
    const evaluationBytes = fromBase64Url(evaluatedBase64);
    const evaluation = Evaluation.deserialize(VOPRF_SUITE, evaluationBytes);

    if (!evaluation.proof) {
      throw new Error('VOPRF evaluation missing proof');
    }

    const expectedProof = evaluation.proof.serialize();
    const providedProof = fromBase64Url(proofBase64);

    if (expectedProof.length !== providedProof.length) {
      throw new Error('Proof length mismatch');
    }

    // Constant-time comparison
    let diff = 0;
    for (let i = 0; i < expectedProof.length; i += 1) {
      diff |= expectedProof[i] ^ providedProof[i];
    }

    if (diff !== 0) {
      throw new Error('Proof mismatch');
    }
  }

  /**
   * Finalize VOPRF evaluation
   * Unblinds the server's response
   *
   * @param evaluatedBase64 - Server's evaluation (base64url)
   * @returns VOPRF output bytes
   */
  async finalize(evaluatedBase64: string): Promise<Uint8Array> {
    const evaluationBytes = fromBase64Url(evaluatedBase64);
    const evaluation = Evaluation.deserialize(VOPRF_SUITE, evaluationBytes);

    if (!evaluation.proof) {
      throw new Error('VOPRF evaluation missing proof');
    }

    const outputs = await this.client.finalize(this.finalizeData, evaluation);

    if (!outputs.length) {
      throw new Error('No VOPRF outputs produced');
    }

    return outputs[0];
  }
}

/**
 * Deserialize evaluation request from base64url
 */
export function deserializeEvaluationRequest(base64: string): EvaluationRequest {
  const bytes = fromBase64Url(base64);
  return EvaluationRequest.deserialize(VOPRF_SUITE, bytes);
}

/**
 * Serialize evaluation to base64url
 */
export function serializeEvaluation(evaluation: Evaluation): string {
  return toBase64Url(evaluation.serialize());
}

/**
 * Serialize proof to base64url
 */
export function serializeProof(evaluation: Evaluation): string {
  if (!evaluation.proof) {
    throw new Error('Proof not present');
  }
  return toBase64Url(evaluation.proof.serialize());
}
